/**
 * QL-677 — the "Mandate selection" section of the Mandate component: the one
 * place the goal's template is chosen. A dropdown of the templates for the
 * goal's current risk score and platform (the current one marked), Save, and
 * Clear while a template is selected. A goal never gets a template by itself.
 *
 * QL-695 — on a goal with a bank mandate (a follow-up) the section is two
 * radios: "Use existing mandate" (the client's, ticked while it is in use)
 * and "Select from mandate templates" with the dropdown under it. Saving a
 * template proposes it beside the bank's; saving "Use existing" removes the
 * proposal (the Clear route) — so there is no Clear button on that goal.
 *
 * The subtitle says where the mandate comes from: nothing yet, a template, the
 * bank's, or a custom portfolio (then the dropdown is empty — picking a
 * template replaces it). Disabled with a hint while the goal has no risk score
 * or nothing matches. Read-only on a locked instance.
 */

import type {
  GoalMandateItem,
  MandateSelectionState,
  MandateTemplateCandidate,
} from "@repo/shared/types";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { OptionRow } from "@/components/ui/option-row";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { apiErrorMessage } from "@/lib/api/_client";
import {
  useClearMandateTemplate,
  useSelectMandateTemplate,
} from "@/lib/hooks/use-flow-instances/use-mandate";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./mandate-selection-section.i18n";

type Props = {
  instanceId: string;
  goalId: string;
  selection: MandateSelectionState;
  candidates: MandateTemplateCandidate[];
  mandate: GoalMandateItem | null;
  /** QL-677 batch 5 — the portfolio the custom mandate follows, for the subtitle. */
  basisPortfolioId: string | null;
  /** The portfolio page's portfolio; null on the goal page. */
  portfolioId: string | null;
  disabled: boolean;
};

/** QL-695 — the radio on a bank-mandate goal; "" while a custom proposal sits beside the bank's. */
type Choice = "existing" | "template" | "";

function choiceOf(selection: MandateSelectionState): Choice {
  if (!selection.existing) return "template";
  if (selection.usingExisting) return "existing";
  return selection.templateId ? "template" : "";
}

export function MandateSelectionSection({
  instanceId,
  goalId,
  selection,
  candidates,
  mandate,
  basisPortfolioId,
  portfolioId,
  disabled,
}: Props) {
  const t = useT(T);
  const localize = useLocalize();
  // The dropdown's pick and the radio, synced from the server until Save.
  const [value, setValue] = useState<string>(selection.templateId ?? "");
  const [choice, setChoice] = useState<Choice>(choiceOf(selection));
  const { existing, usingExisting, templateId } = selection;
  const existingId = existing?.id ?? null;
  useEffect(() => {
    setValue(templateId ?? "");
    setChoice(!existingId ? "template" : usingExisting ? "existing" : templateId ? "template" : "");
  }, [existingId, usingExisting, templateId]);

  const select = useSelectMandateTemplate(instanceId, goalId);
  const clear = useClearMandateTemplate(instanceId, goalId);
  const isSaving = select.isPending || clear.isPending;

  const custom = !!mandate && mandate.source !== "template" && mandate.source !== "bank";
  const subtitle = existing
    ? usingExisting
      ? t.selectionSubtitleExisting
      : custom
        ? portfolioId
          ? basisPortfolioId === portfolioId
            ? t.selectionSubtitleCustomThis
            : t.selectionSubtitleCustomOther
          : t.selectionSubtitleCustomGoal
        : t.selectionSubtitleTemplateExisting
    : !mandate
      ? t.selectionSubtitleNone
      : !custom
        ? t.selectionSubtitleTemplate
        : portfolioId
          ? basisPortfolioId === portfolioId
            ? t.selectionSubtitleCustomThis
            : t.selectionSubtitleCustomOther
          : t.selectionSubtitleCustomGoal;
  const hint =
    selection.reason === "no_risk_score"
      ? t.selectionNoRiskScore
      : selection.reason === "no_templates"
        ? t.selectionNoTemplates
        : null;

  const templateChanged = selection.canSelect && !!value && value !== selection.templateId;
  const canSave = existing
    ? choice === "existing"
      ? !usingExisting
      : choice === "template" && templateChanged
    : templateChanged;

  const save = async () => {
    try {
      if (existing && choice === "existing") {
        const result = await clear.mutateAsync();
        if (!result.success) {
          toast.error(t.selectionError, { description: result.error });
          return;
        }
        toast.success(t.selectionUsingExisting);
        return;
      }
      const result = await select.mutateAsync(value);
      if (!result.success) {
        toast.error(t.selectionError, { description: result.error });
        return;
      }
      toast.success(t.selectionSaved);
    } catch (err) {
      toast.error(t.selectionError, { description: apiErrorMessage(err, t.selectionError) });
    }
  };

  const clearSelection = async () => {
    try {
      const result = await clear.mutateAsync();
      if (!result.success) {
        toast.error(t.selectionError, { description: result.error });
        return;
      }
      toast.success(t.selectionCleared);
    } catch (err) {
      toast.error(t.selectionError, { description: apiErrorMessage(err, t.selectionError) });
    }
  };

  const dropdown = (
    <div className="min-w-0 flex-1 space-y-1 sm:max-w-md">
      <Select
        value={value}
        onValueChange={setValue}
        disabled={
          disabled || !selection.canSelect || isSaving || (!!existing && choice !== "template")
        }
      >
        <SelectTrigger aria-label={t.selectionTitle}>
          <SelectValue placeholder={t.selectionPlaceholder} />
        </SelectTrigger>
        <SelectContent>
          {candidates.map((c) => (
            <SelectItem key={c.id} value={c.id}>
              <span className="flex items-center gap-2">
                <span>{localize(c.nameI18n)}</span>
                <Badge variant="secondary">{c.mode === "fixed" ? t.modeFixed : t.modeMinMax}</Badge>
                {c.id === selection.templateId && (
                  <span className="text-xs text-muted-foreground">{t.selectionCurrent}</span>
                )}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );

  const saveButton = !disabled && (
    <Button size="sm" onClick={save} loading={isSaving} disabled={!canSave}>
      {t.save}
    </Button>
  );

  if (existing) {
    const existingHint = t.selectionUseExistingHint({
      name: existing.name,
      mode: existing.mode === "fixed" ? t.modeFixed : t.modeMinMax,
    });
    return (
      <div className="space-y-3 border rounded-lg p-4">
        <div className="space-y-1">
          <h4 className="text-base font-medium leading-none">{t.selectionTitle}</h4>
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        </div>
        <RadioGroup
          value={choice}
          onValueChange={(next) => setChoice(next as Choice)}
          disabled={disabled || isSaving}
        >
          <OptionRow align="start">
            <RadioGroupItem value="existing" id={`${goalId}-use-existing`} className="mt-0.5" />
            <Label htmlFor={`${goalId}-use-existing`} className="font-normal cursor-pointer">
              {t.selectionUseExisting}
              <span className="block text-xs text-muted-foreground">{existingHint}</span>
            </Label>
          </OptionRow>
          <OptionRow align="start">
            <RadioGroupItem value="template" id={`${goalId}-use-template`} className="mt-0.5" />
            <Label htmlFor={`${goalId}-use-template`} className="font-normal cursor-pointer">
              {t.selectionUseTemplate}
              <span className="block text-xs text-muted-foreground">
                {t.selectionUseTemplateHint}
              </span>
            </Label>
          </OptionRow>
        </RadioGroup>
        <div className="flex flex-col gap-3 pl-6 sm:flex-row sm:items-center sm:justify-between">
          {dropdown}
          {saveButton && <div className="flex shrink-0 justify-end">{saveButton}</div>}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 border rounded-lg p-4">
      <div className="space-y-1">
        <h4 className="text-base font-medium leading-none">{t.selectionTitle}</h4>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {dropdown}
        {!disabled && (
          <div className="flex shrink-0 justify-end gap-2">
            {selection.templateId && (
              <Button variant="outline" size="sm" onClick={clearSelection} loading={isSaving}>
                {t.clear}
              </Button>
            )}
            {saveButton}
          </div>
        )}
      </div>
    </div>
  );
}
